import { useState } from 'react';
import pennyImg from '../../../../../assets/currency/penny.png';
import nickelImg from '../../../../../assets/currency/nickel.png';
import dimeImg from '../../../../../assets/currency/dime.png';
import quarterImg from '../../../../../assets/currency/quarter.png';
import halfImg from '../../../../../assets/currency/half.png';
import dollarImg from '../../../../../assets/currency/dollar.png';
import styles from './CurrencySection.module.css';

type Coin = 'penny' | 'nickel' | 'dime' | 'quarter' | 'half' | 'dollar';

interface CoinInstance {
  id: string;
  kind: Coin;
}

const COINS: { kind: Coin; label: string; centValue: number; img: string }[] = [
  { kind: 'penny', label: 'Penny', centValue: 1, img: pennyImg },
  { kind: 'nickel', label: 'Nickel', centValue: 5, img: nickelImg },
  { kind: 'dime', label: 'Dime', centValue: 10, img: dimeImg },
  { kind: 'quarter', label: 'Quarter', centValue: 25, img: quarterImg },
  { kind: 'half', label: 'Half', centValue: 50, img: halfImg },
  { kind: 'dollar', label: 'Dollar', centValue: 100, img: dollarImg },
];

let nextCoinId = 0;
function newCoin(kind: Coin): CoinInstance {
  nextCoinId += 1;
  return { id: `c-${nextCoinId}`, kind };
}

export function CurrencySection() {
  const [coins, setCoins] = useState<CoinInstance[]>([]);

  function add(kind: Coin) {
    setCoins((prev) => [...prev, newCoin(kind)]);
  }

  function remove(id: string) {
    setCoins((prev) => prev.filter((c) => c.id !== id));
  }

  function clear() {
    setCoins([]);
  }

  return (
    <section className={styles.section} data-testid="currency-section">
      <header className={styles.header}>
        <h2 className={styles.heading}>Currency</h2>
        <button
          type="button"
          onClick={clear}
          className={styles.clearButton}
          data-testid="coin-clear"
        >
          Clear coins
        </button>
      </header>

      <div className={styles.controls} role="group" aria-label="Add coin">
        {COINS.map(({ kind, label, img }) => (
          <button
            key={kind}
            type="button"
            onClick={() => add(kind)}
            className={`${styles.addButton} ${styles[`add_${kind}`]}`}
            data-testid={`coin-add-${kind}`}
            aria-label={`Add a ${label.toLowerCase()}`}
          >
            <img src={img} alt="" className={styles.addImg} />
            <span className={styles.addLabel}>{label}</span>
          </button>
        ))}
      </div>

      {/* Empty tray below the money options — coin/bill instances drop
       *  in here when added, and tap-to-remove. Styled like the
       *  number-modeling bins (dashed border, soft white fill) so
       *  the two sections feel like a matched pair. */}
      <div className={styles.tray} data-testid="coin-tray">
        {coins.map((c) => {
          const meta = COINS.find((k) => k.kind === c.kind);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => remove(c.id)}
              className={`${styles.coin} ${styles[c.kind]}`}
              aria-label={`Remove ${c.kind}`}
              data-testid={`coin-${c.kind}`}
              data-coin-id={c.id}
            >
              {meta ? <img src={meta.img} alt="" className={styles.coinImg} /> : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
